import { useState, useEffect, useRef } from "react";
import ProductCard from "./ProductCard";
import styles from "./Collection.module.css";
import rings from "../data/rings";
import pendants from "../data/pendants";

const allProducts = [
  ...rings.map(r => ({ ...r, category: "ring" })),
  ...pendants.map(p => ({ ...p, category: "pendant" })),
];

const PRICE_RANGES = [
  { label: "All", min: 0, max: Infinity },
  { label: "Under LKR 20k", min: 0, max: 20000 },
  { label: "LKR 20k – 30k", min: 20000, max: 30000 },
  { label: "LKR 30k – 50k", min: 30000, max: 50000 },
  { label: "Over LKR 50k", min: 50000, max: Infinity },
];

const SORT_OPTIONS = [
  { value: "featured", label: "Featured" },
  { value: "low", label: "Price: Low to High" },
  { value: "high", label: "Price: High to Low" },
];

export default function Collection() {
  const [activeTab, setActiveTab] = useState("pendant");
  const [sort, setSort] = useState("featured");
  const [priceRange, setPriceRange] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const filtersRef = useRef(null);
  const sortRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClick = (e) => {
      if (filtersRef.current && !filtersRef.current.contains(e.target)) setFiltersOpen(false);
      if (sortRef.current && !sortRef.current.contains(e.target)) setSortOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  let products = allProducts.filter(p => p.category === activeTab);

  const { min, max } = PRICE_RANGES[priceRange];
  products = products.filter(p => p.price >= min && p.price <= max);

  if (sort === "low") products = [...products].sort((a, b) => a.price - b.price);
  if (sort === "high") products = [...products].sort((a, b) => b.price - a.price);

  const currentSortLabel = SORT_OPTIONS.find(o => o.value === sort)?.label || "Featured";

  return (
    <section id="collection" className={styles.section}>
      <div className={styles.header}>
        <p className={styles.eyebrow}>Our Collection</p>
        <h2 className={styles.title}>Handcrafted Treasures</h2>
        <div className={styles.divider} />
      </div>

      {/* Category tabs */}
      <div className={styles.tabBar}>
        <div className={styles.tabGroup}>
          {["pendant", "ring"].map((tab) => (
            <button key={tab}
              className={`${styles.tab} ${activeTab === tab ? styles.tabActive : ""}`}
              onClick={() => { setActiveTab(tab); setSort("featured"); setPriceRange(0); }}>
              {tab.charAt(0).toUpperCase() + tab.slice(1)}s
            </button>
          ))}
        </div>
      </div>

      {/* Filter + Sort toolbar */}
      <div className={styles.toolBar}>
        {/* Filters */}
        <div className={styles.filterWrap} ref={filtersRef}>
          <button
            className={`${styles.filterBtn} ${priceRange !== 0 ? styles.filterBtnHasActive : ""}`}
            onClick={() => { setFiltersOpen(!filtersOpen); setSortOpen(false); }}
          >
            Filters
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>
            </svg>
            {priceRange !== 0 && <span className={styles.filterDot} />}
          </button>

          {filtersOpen && (
            <div className={styles.filterPanel}>
              <div className={styles.filterPanelHeader}>
                <span className={styles.filterPanelTitle}>Filters</span>
                <button className={styles.filterPanelClose} onClick={() => setFiltersOpen(false)}>✕</button>
              </div>

              <p className={styles.filterSectionLabel}>Price</p>
              <div className={styles.radioGroup}>
                {PRICE_RANGES.map((range, i) => (
                  <button
                    key={i}
                    type="button"
                    className={styles.radioRow}
                    onClick={() => { setPriceRange(i); setFiltersOpen(false); }}
                  >
                    <span className={`${styles.radio} ${priceRange === i ? styles.radioChecked : ""}`}>
                      {priceRange === i && <span className={styles.radioInner} />}
                    </span>
                    <span className={styles.radioLabel}>{range.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sort */}
        <div className={styles.sortWrap} ref={sortRef}>
          <span className={styles.sortLabel}>Sort by</span>
          <button className={styles.sortSelect} onClick={() => { setSortOpen(!sortOpen); setFiltersOpen(false); }}>
            {currentSortLabel}
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M6 9l6 6 6-6"/>
            </svg>
          </button>

          {sortOpen && (
            <div className={styles.sortPanel}>
              {SORT_OPTIONS.map(opt => (
                <label key={opt.value} className={styles.radioRow} onClick={() => { setSort(opt.value); setSortOpen(false); }}>
                  <span className={`${styles.radio} ${sort === opt.value ? styles.radioChecked : ""}`}>
                    {sort === opt.value && <span className={styles.radioInner} />}
                  </span>
                  <span className={styles.radioLabel}>{opt.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {products.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No products in this price range.</p>
          <button className={styles.clearBtn} onClick={() => { setPriceRange(0); }}>Clear Filter</button>
        </div>
      ) : (
        <div className={styles.grid}>
          {products.map((item, i) => (
            <ProductCard key={item.id} item={item} index={i} />
          ))}
        </div>
      )}
    </section>
  );
}